// src/UserPrefsProvider.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthProvider';
import { applyTheme } from './utils/applyTheme';
import dexie from './offlineDB';
import { addPendingWrite } from './utils/flushPendingWrites'; // ✅ this is your real file
import { getModeLabels } from './utils/modeLabels';
import { resolveUsername, isFollowing, followUser } from './utils/socialFirestore';

const UserPrefsContext = createContext({
  prefs: null,
  setPrefs: () => {},
  savePrefs: async () => {}
});

export function useUserPrefs() {
  return useContext(UserPrefsContext);
}

export function UserPrefsProvider({ children }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);

 useEffect(() => {
  if (!user) return;

  const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');
  console.log('[UserPrefsProvider] Listening to:', prefsRef.path);

  const fallback = {
    defaultGenre: '',
    defaultType: '',
    defaultGenreByMode: { tunes: getModeLabels('tunes').defaultGenre, notes: getModeLabels('notes').defaultGenre },
    defaultTypeByMode: { tunes: getModeLabels('tunes').defaultType, notes: getModeLabels('notes').defaultType },
    addTuneFieldsByMode: { tunes: {}, notes: {} },
    defaultKey: '',
    defaultStatus: '',
    showAbcInSearch: true,
    showLyricsInSearch: false,
    showTagPreviewInSearch: true,
    showAbcInSets: true,
    showLyricsInSets: true,
    showTagPreviewInSets: true,
    showSetDateInSets: true,
    showTagPreviewInSessions: true,
    showSessionDateInSessions: true,
    searchTools: [
      { id: 'folkfriend', url: 'folkfriend.app', enabled: true, builtIn: true },
      { id: 'tunepal', url: 'tunepal.org', enabled: true, builtIn: true },
      { id: 'tunearch', url: 'tunearch.org', enabled: true, builtIn: true },
      { id: 'bluegrasslyrics', url: 'bluegrasslyrics.com', enabled: true, builtIn: true },
      { id: 'thesession', url: 'thesession.org', enabled: true, builtIn: true },
    ],
    theme: 'light',
    customBgColor: '#ffffff',
    // Onboarding fields (new users)
    onboardingComplete: false,
    complexityLevel: 'simple',
    dismissedNudges: {},
  };

  // Helper: load prefs from Dexie (offline cache) or use fallback
  const loadFromDexie = () => {
    dexie.preferences.get('settings').then((localPrefs) => {
      if (localPrefs) {
        console.log('[UserPrefsProvider] Loaded prefs from Dexie:', localPrefs);
        setPrefs(p => p || localPrefs); // don't overwrite if Firestore already resolved
        applyTheme(localPrefs.theme, localPrefs.customBgColor, localPrefs.appMode);
        if (localPrefs.theme) localStorage.setItem('theme', localPrefs.theme);
        if (localPrefs.customBgColor) localStorage.setItem('customBgColor', localPrefs.customBgColor);
      } else {
        console.warn('[UserPrefsProvider] Dexie empty. Setting fallback.');
        setPrefs(p => p || fallback);
        applyTheme(fallback.theme, fallback.customBgColor, fallback.appMode);
      }
    });
  };

  if (navigator.onLine) {
    let resolved = false;

    // Safety timeout: if Firestore hasn't responded in 5s, fall back to Dexie.
    // This handles cases where navigator.onLine is true but network is unavailable.
    const timeout = setTimeout(() => {
      if (!resolved) {
        console.warn('[UserPrefsProvider] Firestore timeout, falling back to Dexie.');
        loadFromDexie();
      }
    }, 5000);

    const unsubscribe = onSnapshot(
      prefsRef,
      (snap) => {
        resolved = true;
        clearTimeout(timeout);
        if (snap.exists()) {
          const data = snap.data();
          console.log('[UserPrefsProvider] Firestore data loaded:', data);
          // Migration: existing users who dismissed old tour get full mode
          if (data.tutorialDismissed && data.complexityLevel === undefined) {
            data.complexityLevel = 'full';
          }
          setPrefs(data);
          applyTheme(data.theme, data.customBgColor, data.appMode);
          if (data.theme) localStorage.setItem('theme', data.theme);
          if (data.customBgColor) localStorage.setItem('customBgColor', data.customBgColor);
          dexie.preferences.put({ id: 'settings', ...data });
        } else {
          console.warn('[UserPrefsProvider] No prefs doc found. Setting fallback.');
          setDoc(prefsRef, fallback).catch(console.error);
          setPrefs(fallback);
          applyTheme(fallback.theme, fallback.customBgColor, fallback.appMode);
        }
      },
      (error) => {
        resolved = true;
        clearTimeout(timeout);
        console.error('[UserPrefsProvider] onSnapshot failed:', error);
        loadFromDexie();
      }
    );
    return () => { unsubscribe(); clearTimeout(timeout); };
  } else {
    loadFromDexie();
  }
}, [user]);

// Auto-follow the "allmytunes" account so new users see sampler content
useEffect(() => {
  if (!user || !navigator.onLine) return;
  const key = `autoFollowed_allmytunes_${user.uid}`;
  if (localStorage.getItem(key)) return;

  (async () => {
    try {
      const targetUid = await resolveUsername('allmytunes');
      if (!targetUid || targetUid === user.uid) return;
      const already = await isFollowing(user.uid, targetUid);
      if (!already) await followUser(user.uid, targetUid, []);
      localStorage.setItem(key, '1');
    } catch (err) {
      console.warn('[UserPrefsProvider] Auto-follow allmytunes failed:', err);
    }
  })();
}, [user]);

async function savePrefs(newPrefs) {
  const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');
  if (navigator.onLine) {
    await setDoc(prefsRef, newPrefs, { merge: true });
  } else {
    await dexie.preferences.put({ id: 'settings', ...newPrefs });
    await addPendingWrite({
      type: 'update',
      collection: 'preferences',
      docId: 'settings',
      data: newPrefs
    });
  }
  setPrefs(newPrefs); // update local state regardless
}

// Field-level update — only writes the provided keys, never touches other fields.
// Use this instead of savePrefs when updating individual settings so that
// multi-device sessions can't overwrite each other's appMode or other fields.
async function patchPrefs(patch) {
  const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');
  if (navigator.onLine) {
    await setDoc(prefsRef, patch, { merge: true });
  } else {
    const current = (await dexie.preferences.get('settings')) || {};
    await dexie.preferences.put({ ...current, id: 'settings', ...patch });
    await addPendingWrite({
      type: 'update',
      collection: 'preferences',
      docId: 'settings',
      data: patch
    });
  }
  setPrefs(prev => ({ ...(prev || {}), ...patch }));
}

  const currentMode = prefs?.appMode || 'tunes';
  const labels = getModeLabels(currentMode);
  const modeGenres = Array.isArray(prefs?.customGenres)
    ? prefs.customGenres  // legacy flat array — will be migrated by Settings
    : currentMode === 'all'
      ? [...new Set([...(prefs?.customGenres?.tunes || []), ...(prefs?.customGenres?.notes || [])])]
      : prefs?.customGenres?.[currentMode] || [];
  const modeTypes = Array.isArray(prefs?.customTypes)
    ? prefs.customTypes
    : currentMode === 'all'
      ? [...new Set([...(prefs?.customTypes?.tunes || []), ...(prefs?.customTypes?.notes || [])])]
      : prefs?.customTypes?.[currentMode] || [];

  return (
<UserPrefsContext.Provider value={{ prefs, setPrefs, savePrefs, patchPrefs, labels, modeGenres, modeTypes }}>
      {children}
    </UserPrefsContext.Provider>
  );
}
